import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, useChartHeight, useYAxisDomain, XAxis, YAxis } from "recharts";
import { useTheme } from "../../contexts/theme";
import { TbAlertSquareRounded } from "react-icons/tb";

const chartMargin = { top: 5, right: 20, left: -10, bottom: 5 };
const tooltipCursorStyle = { fill: 'rgba(128, 128, 128, 0.1)' };
const legendWrapperStyle = { fontSize: '14px', left: 10, bottom: '-10px' };
const barRadius = [4, 4, 0, 0];

const Gradient = () => {
   const yDomain = useYAxisDomain();
   const height = useChartHeight();
   if (yDomain == null || height == null) {
      return null;
   }
   const ratio = yDomain[1] / height;
   return (
      <defs>
         <linearGradient id="splitColor" x1="0" x2="0" y1="0" y2={height} gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="var(--upcomingDueLicensesColor)" stopOpacity={0.5} />
            {/* <stop offset={`${ratio}`} stopColor="var(--upcomingDueLicensesColor)" stopOpacity={0.1} /> */}
            <stop offset="1" stopColor="var(--upcomingDueLicensesColor)" stopOpacity={0} />
         </linearGradient>
      </defs>
   );
};

export const CustomTooltip = ({ active, payload, label }) => {
   const { themeName } = useTheme();
   const tooltipStyle = useMemo(
      () => ({
         backgroundColor: `${themeName?.name === 'black' ? '#15191dff' : 'rgb(241, 239, 239)'}`,
         border: `1px solid var(--borderColor)`,
         borderRadius: '0.5rem',
         padding: '0.5rem 1rem',
         boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      }),
      [themeName]
   );

   if (active && payload && payload.length) {
      return (
         <div style={tooltipStyle} className="text-sm">
            <p className="label font-bold mb-1! text-(--textColor)">{`${label}`}</p>
            {payload.map((pld, index) => {
               return <p key={index} style={{ color: pld.color }} className="mb-0.5!">{`${pld.name}: ${pld.value}`}</p>
            })}
         </div>
      );
   }
   return null;
};

export function UpcomingDueDates({ overdues: overduesProps, ...props }) {
   const { themeName } = useTheme();
   const axisTickStyle = useMemo(() => ({ fill: themeName?.name === 'black' ? 'rgb(156 163 175)' : 'rgb(107 114 128)', fontSize: 12 }), [themeName]);
   const gridStroke = useMemo(() => (themeName?.name === 'black' ? 'rgba(55, 65, 81, 0.5)' : 'rgba(229, 231, 235, 1)'), [themeName]);

   function generateMockOverdues() {
      const overdues = [];
      const now = new Date();

      for (let day = 1; day <= 10; day++) {
         now.setDate(now.getDate() + 1);
         overdues.push({
            day: `${now.getDate()}/${now.getMonth() + 1}`,
            total: Math.floor(Math.random() * 100)
         });
      }

      return overdues;
   }

   // const overdues = useMemo(() => generateMockOverdues(), []);

   const overdues = overduesProps?.map(overdue => {
      const date = new Date(overdue.date);
      return {
         day: `${date.getDate()}/${date.getMonth() + 1}`,
         total: overdue.quantity,
      }
   });

   const maxY = Math.max(...overdues.map(item => item.total + 2));

   return (
      <div className="w-full h-fit border! border-(--borderColor)! rounded-(--borderRadius)! **:text-(--textColor) box-border! shadow-(--box-shadow) bg-(--containerBackgroundColor)">
         <div className="flex gap-2 items-center w-full border-b! border-(--borderColor)! px-5! py-4! box-border! font-bold! text-[0.9rem]!">
            <div className="inline-flex flex-row items-center gap-2">
               <TbAlertSquareRounded size={19} />
               <span className="font-bold! leading-[0.1]!">Vencimentos próximos</span>
            </div>
            <span className="font-normal! opacity-60 text-sm! leading-[1rem]!">(Próximos 10 dias)</span>
         </div>
         <div className="px-7! pt-7! pb-5! w-full h-[240px] -ml-10! box-border!">
            <ResponsiveContainer className={`w-[calc(100%+40px)]! h-full!`}>
               <BarChart
                  responsive
                  data={overdues}
                  margin={{
                     left: 0,
                     right: 0,
                  }}
               >
                  <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <YAxis
                     dataKey="total"
                     domain={[0, maxY]}
                     tick={axisTickStyle}
                     tickLine={false}
                     axisLine={false}
                     tickMargin={8}
                     allowDecimals={false}
                  />

                  <XAxis
                     dataKey="day"
                     tick={axisTickStyle}
                     tickLine={false}
                     axisLine={false}
                     tickMargin={8}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={tooltipCursorStyle} />
                  <Gradient />

                  <Bar
                     name="Licenças vencendo"
                     dataKey="total"
                     fill="url(#splitColor)"
                     stroke="var(--upcomingDueLicensesColor)"
                     strokeWidth={2}
                     radius={[4, 4, 0, 0]}
                  />
               </BarChart>
            </ResponsiveContainer>
         </div>
      </div>
   );
}